import { ZONE_THRESHOLDS } from './constants';

const HISTOGRAM_LENGTH = 256;
const DEFAULT_SIGNATURE_BINS = 32;
const DEFAULT_QUANTILES = [0.01, 0.05, 0.1, 0.25, 0.5, 0.75, 0.9, 0.95, 0.99];

const DEFAULT_PEAK_OPTIONS = {
    smoothingWindow: 2,
    minProminenceRatio: 0.0006,
    prominenceWindow: 16,
    minPeakDistance: 4,
    minWidth: 2,
    maxWidth: 30,
    minHeightRatio: 0.0002,
    minHeightToGlobalMaxRatio: 0.03,
    snapToRawWindow: 4,
    edgePadding: 2,
    maxPeaks: 5,
    peakScoreMode: 'prominence_width'
};

const ZONE_KEYS = ['blacks', 'shadows', 'midtones', 'highlights', 'whites'];

function sanitizeHistogram(histogram) {
    const source = Array.isArray(histogram) ? histogram : [];
    const sanitized = new Array(HISTOGRAM_LENGTH).fill(0);
    for (let i = 0; i < HISTOGRAM_LENGTH; i++) {
        const raw = Number(source[i] ?? 0);
        sanitized[i] = Number.isFinite(raw) && raw > 0 ? raw : 0;
    }
    return sanitized;
}

function sumArray(values) {
    return values.reduce((acc, value) => acc + value, 0);
}

function clampInteger(value, min, max) {
    return Math.min(Math.max(Math.round(Number(value) || 0), min), max);
}

function resolveZoneThresholds(zoneThresholds = {}) {
    const blacks = clampInteger(zoneThresholds.BLACKS ?? ZONE_THRESHOLDS.BLACKS, 0, 255);
    const shadows = clampInteger(zoneThresholds.SHADOWS ?? ZONE_THRESHOLDS.SHADOWS, blacks, 255);
    const midtones = clampInteger(zoneThresholds.MIDTONES ?? ZONE_THRESHOLDS.MIDTONES, shadows, 255);
    const highlights = clampInteger(zoneThresholds.HIGHLIGHTS ?? ZONE_THRESHOLDS.HIGHLIGHTS, midtones, 255);

    return { blacks, shadows, midtones, highlights };
}

function getZoneByIntensity(intensity, zoneThresholds = {}) {
    const x = clampInteger(intensity, 0, 255);
    const thresholds = resolveZoneThresholds(zoneThresholds);
    if (x <= thresholds.blacks) return 'blacks';
    if (x <= thresholds.shadows) return 'shadows';
    if (x <= thresholds.midtones) return 'midtones';
    if (x <= thresholds.highlights) return 'highlights';
    return 'whites';
}

function movingAverage(values, windowSize = 3) {
    const safeWindow = Math.max(1, Math.floor(windowSize));
    const radius = Math.floor(safeWindow / 2);
    if (radius <= 0) {
        return [...values];
    }

    const result = new Array(values.length).fill(0);
    for (let i = 0; i < values.length; i++) {
        let sum = 0;
        let count = 0;
        for (let j = i - radius; j <= i + radius; j++) {
            if (j < 0 || j >= values.length) continue;
            sum += values[j];
            count += 1;
        }
        result[i] = count > 0 ? sum / count : 0;
    }
    return result;
}

function roundTo(value, digits = 6) {
    const factor = 10 ** digits;
    return Math.round((Number(value) || 0) * factor) / factor;
}

function isLocalPeak(prev, curr, next) {
    return (curr >= prev && curr > next) || (curr > prev && curr >= next);
}

function pickByMinDistance(candidates, minDistance, scoreKey, maxCount) {
    const safeDistance = Math.max(0, Math.floor(Number(minDistance) || 0));
    const safeMaxCount = Math.max(0, Math.floor(Number(maxCount) || 0));
    if (safeMaxCount === 0 || !Array.isArray(candidates) || candidates.length === 0) {
        return [];
    }

    const sorted = [...candidates].sort((a, b) => (Number(b?.[scoreKey]) || 0) - (Number(a?.[scoreKey]) || 0));
    const selected = [];

    for (const item of sorted) {
        const x = Number(item?.x);
        if (!Number.isFinite(x)) continue;
        const overlaps = selected.some((picked) => Math.abs((Number(picked?.x) || 0) - x) < safeDistance);
        if (overlaps) continue;
        selected.push(item);
        if (selected.length >= safeMaxCount) break;
    }

    return selected;
}

function findLeftMin(values, start, end) {
    let min = Number.POSITIVE_INFINITY;
    for (let i = start; i <= end; i++) {
        min = Math.min(min, values[i]);
    }
    return Number.isFinite(min) ? min : 0;
}

function measurePeakWidth(values, index, level, maxSpan = values.length) {
    const safeSpan = Math.max(1, Math.floor(Number(maxSpan) || values.length));
    const leftBound = Math.max(0, index - safeSpan);
    const rightBound = Math.min(values.length - 1, index + safeSpan);

    let left = index;
    while (left > leftBound && values[left - 1] >= level) {
        left -= 1;
    }

    let right = index;
    while (right < rightBound && values[right + 1] >= level) {
        right += 1;
    }

    return right - left + 1;
}

function computePeakScore(peak, mode = 'prominence_width') {
    const prominence = Math.max(0, Number(peak?.prominence) || 0);
    const width = Math.max(1, Number(peak?.width) || 1);
    const height = Math.max(0, Number(peak?.heightRatio) || 0);

    if (mode === 'prominence') return prominence;
    if (mode === 'height') return height;
    return prominence * Math.sqrt(width);
}

function findRangeMin(values, start, end) {
    const left = Math.max(0, Math.min(start, end));
    const right = Math.min(values.length - 1, Math.max(start, end));
    return findLeftMin(values, left, right);
}

function argMaxIndexInRange(values, center, radius) {
    const left = Math.max(0, Math.floor(center - radius));
    const right = Math.min(values.length - 1, Math.ceil(center + radius));
    let bestIndex = left;
    let bestValue = Number.NEGATIVE_INFINITY;

    for (let i = left; i <= right; i++) {
        const value = Number(values[i] || 0);
        if (value > bestValue) {
            bestValue = value;
            bestIndex = i;
        }
    }
    return bestIndex;
}

function normalizePositiveArray(values) {
    const safe = Array.isArray(values) ? values.map((v) => Math.max(0, Number(v) || 0)) : [];
    const sum = sumArray(safe);
    if (sum <= 0) {
        return safe.map(() => 0);
    }
    return safe.map((v) => roundTo(v / sum));
}

function quantileKey(q) {
    const label = String(Math.round(q * 100)).padStart(2, '0');
    return `p${label}`;
}

function getTotalPixelsForChannel(channel, totalPixels) {
    const basePixels = Math.max(0, Number(totalPixels) || 0);
    return channel === 'RGB' ? basePixels * 3 : basePixels;
}

export function normalizeHistogram(histogram, totalPixelsForChannel) {
    const sanitized = sanitizeHistogram(histogram);
    const totalFromHistogram = sumArray(sanitized);
    const configuredTotal = Math.max(0, Number(totalPixelsForChannel) || 0);
    const denominator = configuredTotal > 0 ? configuredTotal : totalFromHistogram;

    if (denominator <= 0) {
        return new Array(HISTOGRAM_LENGTH).fill(0);
    }

    return sanitized.map((value) => value / denominator);
}

export function computeQuantilesFromHistogram(histogram, quantileList = DEFAULT_QUANTILES) {
    const sanitized = sanitizeHistogram(histogram);
    const total = sumArray(sanitized);
    const sortedQuantiles = [...quantileList]
        .map((q) => Number(q))
        .filter((q) => Number.isFinite(q) && q >= 0 && q <= 1)
        .sort((a, b) => a - b);

    const result = {};
    if (total <= 0 || sortedQuantiles.length === 0) {
        sortedQuantiles.forEach((q) => {
            result[quantileKey(q)] = 0;
        });
        result.iqr = 0;
        return result;
    }

    let cumulative = 0;
    let pointer = 0;
    for (let intensity = 0; intensity < sanitized.length; intensity++) {
        cumulative += sanitized[intensity];
        while (pointer < sortedQuantiles.length && cumulative >= sortedQuantiles[pointer] * total) {
            result[quantileKey(sortedQuantiles[pointer])] = intensity;
            pointer += 1;
        }
    }

    while (pointer < sortedQuantiles.length) {
        result[quantileKey(sortedQuantiles[pointer])] = HISTOGRAM_LENGTH - 1;
        pointer += 1;
    }

    const p25 = result.p25;
    const p75 = result.p75;
    result.iqr = Number.isFinite(p25) && Number.isFinite(p75) ? p75 - p25 : 0;
    return result;
}

export function compressHistogramSignature(histogram, bins = DEFAULT_SIGNATURE_BINS) {
    const safeBins = Math.max(1, Math.floor(Number(bins) || DEFAULT_SIGNATURE_BINS));
    const normalized = normalizeHistogram(histogram);
    const segmentSize = HISTOGRAM_LENGTH / safeBins;
    const signature = new Array(safeBins).fill(0);

    for (let i = 0; i < HISTOGRAM_LENGTH; i++) {
        const segment = Math.min(safeBins - 1, Math.floor(i / segmentSize));
        signature[segment] += normalized[i];
    }

    return signature.map((value) => roundTo(value));
}

export function detectPeaks(histogram, options = {}) {
    const mergedOptions = {
        ...DEFAULT_PEAK_OPTIONS,
        ...options
    };

    const sanitized = sanitizeHistogram(histogram);
    const normalized = normalizeHistogram(sanitized, mergedOptions.totalPixelsForChannel);
    const normalizedRaw = normalizeHistogram(sanitized, mergedOptions.totalPixelsForChannel);
    const smoothed = movingAverage(normalized, mergedOptions.smoothingWindow);
    const minProminenceRatio = Math.max(0, Number(mergedOptions.minProminenceRatio) || 0);
    const prominenceWindow = Math.max(2, Math.floor(Number(mergedOptions.prominenceWindow) || DEFAULT_PEAK_OPTIONS.prominenceWindow));
    const minWidth = Math.max(1, Math.floor(Number(mergedOptions.minWidth) || DEFAULT_PEAK_OPTIONS.minWidth));
    const maxWidth = Math.max(minWidth, Math.floor(Number(mergedOptions.maxWidth) || DEFAULT_PEAK_OPTIONS.maxWidth));
    const minHeightRatio = Math.max(0, Number(mergedOptions.minHeightRatio) || 0);
    const minHeightToGlobalMaxRatio = Math.max(0, Number(mergedOptions.minHeightToGlobalMaxRatio) || 0);
    const snapToRawWindow = Math.max(0, Number(mergedOptions.snapToRawWindow) || 0);
    const edgePadding = Math.max(0, Math.floor(Number(mergedOptions.edgePadding) || 0));
    const globalMax = Math.max(...smoothed, 0);
    const minHeightByGlobalMax = globalMax * minHeightToGlobalMaxRatio;
    const zoneThresholds = mergedOptions.zoneThresholds || {};

    const peakCandidates = [];
    for (let i = 1 + edgePadding; i < smoothed.length - 1 - edgePadding; i++) {
        const prev = smoothed[i - 1];
        const current = smoothed[i];
        const next = smoothed[i + 1];
        if (!isLocalPeak(prev, current, next)) continue;

        const peakIndex = i;
        const curr = smoothed[peakIndex] || 0;
        const leftBase = findRangeMin(smoothed, peakIndex - prominenceWindow, peakIndex);
        const rightBase = findRangeMin(smoothed, peakIndex, peakIndex + prominenceWindow);
        const base = Math.max(leftBase, rightBase);
        const prominence = curr - base;
        if (prominence < minProminenceRatio) continue;
        if (curr < minHeightRatio) continue;
        if (curr < minHeightByGlobalMax) continue;

        const measuredWidth = measurePeakWidth(smoothed, peakIndex, curr - prominence / 2, prominenceWindow);
        if (measuredWidth < minWidth || measuredWidth > maxWidth) continue;

        const snappedX = snapToRawWindow > 0 ? argMaxIndexInRange(normalizedRaw, peakIndex, snapToRawWindow) : peakIndex;
        const snappedHeightRatio = normalizedRaw[snappedX] || curr;

        const peak = {
            x: snappedX,
            heightRatio: roundTo(snappedHeightRatio),
            prominence: roundTo(prominence),
            width: measuredWidth,
            zone: getZoneByIntensity(snappedX, zoneThresholds)
        };
        peak.score = roundTo(computePeakScore(peak, mergedOptions.peakScoreMode));
        peakCandidates.push(peak);
    }

    const peaks = pickByMinDistance(
        peakCandidates,
        mergedOptions.minPeakDistance,
        'score',
        mergedOptions.maxPeaks
    );

    return {
        peaks: peaks.map(({ score: _score, ...rest }) => rest)
    };
}

export function calculateZoneMassRatios(histogram, totalPixelsForChannel, zoneThresholds = {}) {
    const sanitized = sanitizeHistogram(histogram);
    const thresholds = resolveZoneThresholds(zoneThresholds);
    const total = Math.max(0, Number(totalPixelsForChannel) || 0) || sumArray(sanitized);

    const ranges = {
        blacks: [0, thresholds.blacks],
        shadows: [thresholds.blacks + 1, thresholds.shadows],
        midtones: [thresholds.shadows + 1, thresholds.midtones],
        highlights: [thresholds.midtones + 1, thresholds.highlights],
        whites: [thresholds.highlights + 1, 255]
    };

    const ratios = {};
    ZONE_KEYS.forEach((key) => {
        const [start, end] = ranges[key];
        let zoneSum = 0;
        for (let i = start; i <= end; i++) {
            zoneSum += sanitized[i] || 0;
        }
        ratios[key] = total > 0 ? roundTo(zoneSum / total) : 0;
    });

    return ratios;
}

export function buildHistogramShapeSummary({
    histogram,
    totalPixels,
    channel = 'unknown',
    zoneThresholds = {},
    quantiles = DEFAULT_QUANTILES,
    signatureBins = DEFAULT_SIGNATURE_BINS,
    stepAverages = null,
    peakOptions = {}
}) {
    const totalPixelsForChannel = getTotalPixelsForChannel(channel, totalPixels);
    const quantileStats = computeQuantilesFromHistogram(histogram, quantiles);
    const signature = compressHistogramSignature(histogram, signatureBins);
    const { peaks } = detectPeaks(histogram, {
        ...peakOptions,
        totalPixelsForChannel,
        zoneThresholds
    });

    return {
        channel,
        totalPixelsForChannel,
        quantiles: quantileStats,
        curveSignature32: signature,
        peaks,
        peakMain: peaks.length > 0 ? { x: peaks[0].x, prominence: peaks[0].prominence } : null,
        shapeHint10: Array.isArray(stepAverages) ? normalizePositiveArray(stepAverages) : null,
        zoneMassRatio: calculateZoneMassRatios(histogram, totalPixelsForChannel, zoneThresholds)
    };
}

export function buildCrossChannelSummary({
    rHist,
    gHist,
    bHist,
    totalPixels,
    zoneThresholds = {},
    peakOptions = {}
}) {
    const rSummary = buildHistogramShapeSummary({
        histogram: rHist,
        totalPixels,
        channel: 'R',
        zoneThresholds,
        peakOptions
    });
    const gSummary = buildHistogramShapeSummary({
        histogram: gHist,
        totalPixels,
        channel: 'G',
        zoneThresholds,
        peakOptions
    });
    const bSummary = buildHistogramShapeSummary({
        histogram: bHist,
        totalPixels,
        channel: 'B',
        zoneThresholds,
        peakOptions
    });

    const dominantByZone = {};
    ZONE_KEYS.forEach((zone) => {
        const candidates = [
            { channel: 'r', value: rSummary.zoneMassRatio[zone] || 0 },
            { channel: 'g', value: gSummary.zoneMassRatio[zone] || 0 },
            { channel: 'b', value: bSummary.zoneMassRatio[zone] || 0 }
        ];
        candidates.sort((a, b) => b.value - a.value);
        dominantByZone[zone] = candidates[0].channel;
    });

    const rPeakX = rSummary.peakMain?.x ?? 0;
    const gPeakX = gSummary.peakMain?.x ?? 0;
    const bPeakX = bSummary.peakMain?.x ?? 0;

    const separationZones = ['shadows', 'midtones', 'highlights'];
    const separationValues = separationZones.map((zone) => {
        const values = [
            rSummary.zoneMassRatio[zone] || 0,
            gSummary.zoneMassRatio[zone] || 0,
            bSummary.zoneMassRatio[zone] || 0
        ];
        return Math.max(...values) - Math.min(...values);
    });
    const separation = separationValues.length > 0
        ? sumArray(separationValues) / separationValues.length
        : 0;

    return {
        dominantByZone,
        peakPositionOffset: {
            rVsG: rPeakX - gPeakX,
            rVsB: rPeakX - bPeakX,
            gVsB: gPeakX - bPeakX
        },
        channelSeparationIndex: roundTo(separation)
    };
}

export default {
    normalizeHistogram,
    computeQuantilesFromHistogram,
    compressHistogramSignature,
    detectPeaks,
    calculateZoneMassRatios,
    buildHistogramShapeSummary,
    buildCrossChannelSummary
};
